import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { API_IDS } from '@intigral/constant';
import {
    IProfileAddAndUpdateResponse,
    IProfileGetResponse,
    IProfileSwitchResponse,
    IFavoritesAndWatchlistIdsResponse,
    IFavoritesAndWatchlistDeleteResponse,
    IWatchlistQueryParams,
    IWatchlistGetResponse,
    IProfileSettings,
    IB2CForgotParentalLockStep1Response,
    IB2CForgotParentalLockStep2Response,
    ICreateParentalLockRequest,
    ICreateParentalLockResponse,
    IValidateParentalLockResponse,
    IGetParentalLockResponse,
    IUpdateParentalLockResponse,
    IValidatePasswordResponse,
    IAccountB2CRetrievalGenerateOTPResponse,
    IAccountB2CRetrievalVerifyOTPResponse,
    IResetParentalLockVerifyOTPResponse,
} from '../../entity/interface/iProfile';
import { container } from '../../DataDomain';
import { IApiPathManager } from '../config/ApiPathManager';
import { DI } from '@intigral/constant/config/DIContainer';
import { IProfileRepository } from '../../core/repositories/interfaces/iProfileRepository';
import type { IProfileData } from '@intigral/types';

class ProfileRepository implements IProfileRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async handleProtectedProfile(profile: IProfileData, password?: string) {
        const isProtected: boolean = profile.passwordProtected;
        let epochMs: string | undefined = undefined;
        let encPassword: string | undefined = undefined;
        let isPasswordRequired = isProtected;

        if (isProtected) {
            const storedPassword: string | undefined =
                profile?.profilePassword || (typeof password === 'string' ? password : undefined);
            if (storedPassword) {
                epochMs = Date.now().toString();
                encPassword = window.btoa(`${epochMs}:${storedPassword || password}`);
                isPasswordRequired = false;
            }
        }

        return {
            isProtected,
            epochMs,
            encPassword,
            isPasswordRequired,
        };
    }

    async addProfile(
        apiKey: string,
        profileName: string,
        profileSettings: IProfileSettings,
        kidsMode: boolean,
        token: string,
        authSign: string,
        identityToken: string,
        apiToken: { [key: string]: string }
    ): Promise<IProfileAddAndUpdateResponse> {
        const response = await this.http.post<IProfileAddAndUpdateResponse>(
            this.apiPathManager.getPath(API_IDS.PROFILE_ADD),
            {
                params: {
                    apiKey: apiKey,
                },
                headers: {
                    Authorization: token,
                    identityToken,
                },
                body: {
                    apikey: apiKey,
                    profileName,
                    authToken: token,
                    profileSettings,
                    kidsMode,
                    ...apiToken,
                    authSign,
                },
            }
        );
        return response.data;
    }

    async updateProfile(
        apiKey: string,
        guid: string,
        password: string,
        profileName: string,
        profileSettings: IProfileSettings,
        kidsMode: boolean | null,
        token: string,
        authSign: string,
        identityToken: string,
        currentProfile: IProfileData,
        apiToken: { [key: string]: string }
    ): Promise<IProfileAddAndUpdateResponse> {
        const { epochMs, encPassword } = await this.handleProtectedProfile(currentProfile, password);

        const response = await this.http.put<IProfileAddAndUpdateResponse>(
            this.apiPathManager.getPath(API_IDS.PROFILE_UPDATE),
            {
                withCredentials: false,
                params: {
                    apiKey: apiKey,
                    profileGuid: guid,
                },
                headers: {
                    Authorization: token,
                    identityToken,
                    ...(epochMs && encPassword ? { epochMs, encPassword } : {}),
                },
                body: {
                    authToken: token,
                    profileSettings,
                    profileGuid: guid,
                    password: password,
                    profileName,
                    kidsMode: kidsMode,
                    apiKey: apiKey,
                    authSign,
                    ...apiToken,
                    identityToken,
                },
            }
        );
        return response.data;
    }

    async deleteProfile(
        apiKey: string,
        guid: string,
        userPassword: string,
        token: string,
        authSign: string,
        identityToken: string,
        currentProfile: IProfileData,
        csrfToken: { [key: string]: string }
    ): Promise<IProfileAddAndUpdateResponse> {
        const { epochMs, encPassword } = await this.handleProtectedProfile(currentProfile, userPassword);
        const path = this.apiPathManager.getPath(API_IDS.PROFILE_DELETE);
        const response = await this.http.delete<IProfileAddAndUpdateResponse>(path, {
            withCredentials: false,
            params: {
                apiKey: apiKey,
                profileGuid: guid,
            },
            headers: {
                Authorization: token,
                identityToken,
                ...(epochMs && encPassword ? { epochMs, encPassword } : {}),
            },
            body: {
                apiKey: apiKey,
                profileGuid: guid,
                userPassword: userPassword,
                authToken: token,
                authSign,
                ...csrfToken,
            },
        });
        return response.data;
    }

    async getProfiles(
        apiKey: string,
        token: string,
        identityToken: string,
        kidsMode = false
    ): Promise<IProfileGetResponse> {
        const path = this.apiPathManager.getPath(API_IDS.PROFILE_LIST);
        const response = await this.http.get<IProfileGetResponse>(path, {
            params: {
                apiKey: apiKey,
                kidsMode: kidsMode === true,
            },
            headers: {
                Authorization: token,
                identityToken,
            },
        });
        return response.data;
    }

    async switchProfile(
        apiKey: string,
        profileGuid: string,
        currentProfile: IProfileData,
        profilePassword: string,
        token: string,
        identityToken: string
    ): Promise<IProfileSwitchResponse> {
        const basePath = this.apiPathManager.getPath(API_IDS.PROFILE_SWITCH);
        const placeholders = new Map<string, string>();
        placeholders.set('apiKey', apiKey);
        const path = this.apiPathManager.replacePlaceholders(basePath, placeholders);

        const { epochMs, encPassword } = await this.handleProtectedProfile(currentProfile, profilePassword);

        const headers: Record<string, string> = {
            Authorization: token,
            identityToken: identityToken,
            ...(epochMs && encPassword ? { epochMs, encPassword } : {}),
        };

        const body: Record<string, any> = {
            profileGuid: profileGuid,
        };

        const response = await this.http.put<IProfileSwitchResponse>(path, {
            withCredentials: true,
            headers,
            body,
        });

        return response.data;
    }
    async addFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        channels: string[],
        token: string,
        identityToken: string
    ): Promise<IFavoritesAndWatchlistIdsResponse> {
        const response = await this.http.post<IFavoritesAndWatchlistIdsResponse>(
            this.apiPathManager.getPath(API_IDS.PROFILE_FAVORITE),
            {
                params: {
                    apiKey: apiKey,
                    profileGuid,
                },
                headers: {
                    Authorization: token,
                    identityToken: identityToken,
                },
                body: {
                    ids: channels,
                },
            }
        );
        return response.data;
    }
    async getFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        query: object = {}
    ): Promise<IFavoritesAndWatchlistIdsResponse> {
        const response = await this.http.get<IFavoritesAndWatchlistIdsResponse>(
            this.apiPathManager.getPath(API_IDS.PROFILE_FAVORITE),
            {
                params: {
                    apiKey: apiKey,
                    profileGuid,
                    ...query,
                },
                headers: {
                    Authorization: token,
                    identityToken: identityToken,
                },
            }
        );
        return response.data;
    }
    async deleteFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        id: string,
        token: string,
        identityToken: string
    ): Promise<IFavoritesAndWatchlistDeleteResponse> {
        const response = await this.http.delete<IFavoritesAndWatchlistDeleteResponse>(
            this.apiPathManager.getPath(API_IDS.PROFILE_FAVORITE),
            {
                params: {
                    apiKey: apiKey,
                    profileGuid,
                    id: id,
                },
                headers: {
                    Authorization: token,
                    identityToken: identityToken,
                },
            }
        );
        return response.data;
    }
    async addWatchlist(
        type: 'linear' | 'vod',
        apiKey: string,
        profileGuid: string,
        ids: string[],
        token: string,
        identityToken: string
    ): Promise<IFavoritesAndWatchlistIdsResponse> {
        const response = await this.http.post<IFavoritesAndWatchlistIdsResponse>(
            this.apiPathManager.getPath(
                type === 'linear' ? API_IDS.PROFILE_WATCHLIST_LINEAR : API_IDS.PROFILE_WATCHLIST_VOD
            ),
            {
                params: {
                    apiKey: apiKey,
                    profileGuid,
                    type: type,
                    programType: type?.toUpperCase(),
                },
                headers: {
                    Authorization: token,
                    identityToken: identityToken,
                },
                body: {
                    ids: ids,
                },
            }
        );
        return response.data;
    }
    async getWatchlist(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        query: IWatchlistQueryParams = {}
    ): Promise<IWatchlistGetResponse> {
        const response = await this.http.get<IWatchlistGetResponse>(
            this.apiPathManager.getPath(API_IDS.PROFILE_GET_WATCHLIST),
            {
                params: {
                    apiKey: apiKey,
                    type: '',
                    profileGuid,
                    ...query,
                },
                headers: {
                    Authorization: token,
                    identityToken: identityToken,
                },
            }
        );
        return response.data;
    }
    async deleteWatchlist(
        type: 'linear' | 'vod',
        apiKey: string,
        profileGuid: string,
        id: string,
        token: string,
        identityToken: string
    ): Promise<IFavoritesAndWatchlistDeleteResponse> {
        const response = await this.http.delete<IFavoritesAndWatchlistDeleteResponse>(
            this.apiPathManager.getPath(
                type === 'linear' ? API_IDS.PROFILE_WATCHLIST_LINEAR : API_IDS.PROFILE_WATCHLIST_VOD
            ),
            {
                params: {
                    apiKey,
                    type,
                    profileGuid,
                    programType: type.toUpperCase(),
                    id,
                },
                headers: {
                    Authorization: token,
                    identityToken: identityToken,
                },
            }
        );
        return response.data;
    }

    async accountRetrievalGenerateOTP(
        apiKey: string,
        identityToken: string,
        token: string,
        username: string
    ): Promise<IB2CForgotParentalLockStep1Response> {
        const response = await this.http.get<IB2CForgotParentalLockStep1Response>(
            this.apiPathManager.getPath(API_IDS.USER_FORGOT_PASSWORD_GENERATE_PIN_BY_MSISDN_NEW),
            {
                params: {
                    apiKey,
                    userName: username,
                },
                headers: {
                    identityToken,
                    Authorization: token,
                },
            }
        );
        return response.data;
    }
    async accountB2CRetrievalGenerateOTP(username: string): Promise<IAccountB2CRetrievalGenerateOTPResponse> {
        const response = await this.http.post<IAccountB2CRetrievalGenerateOTPResponse>(
            this.apiPathManager.getPath(API_IDS.B2C_RESET_PASSWORD_GENERATE),
            {
                body: {
                    userAgent: 'ios',
                    username: username,
                },
            }
        );
        return response.data;
    }

    async accountB2CRetrievalVerifyOTP(
        apiKey: string,
        otp: string,
        username: string,
        userId?: string,
        productKey?: string
    ): Promise<IAccountB2CRetrievalVerifyOTPResponse> {
        const response = await this.http.post<IAccountB2CRetrievalVerifyOTPResponse>(
            this.apiPathManager.getPath(API_IDS.B2C_RESET_PASSWORD_VERIFY),
            {
                params: {
                    apiKey: apiKey,
                },
                body: {
                    userAgent: 'ios',
                    username,
                    otp,
                    userId,
                    productKey,
                },
            }
        );
        return response.data;
    }

    async accountRetrievalVerifyOTP(
        apiKey: string,
        identityToken: string,
        otp: string
    ): Promise<IB2CForgotParentalLockStep2Response> {
        const response = await this.http.post<IB2CForgotParentalLockStep2Response>(
            this.apiPathManager.getPath(API_IDS.USER_FORGOT_PASSWORD_VERIFIY_BY_MSISDN),
            {
                params: {
                    apiKey,
                },
                body: {
                    otp,
                },
                headers: {
                    identityToken,
                },
            }
        );
        return response.data;
    }

    async createParentalLock(
        apiKey: string,
        identityToken: string,
        request: ICreateParentalLockRequest
    ): Promise<ICreateParentalLockResponse> {
        const path = this.apiPathManager.getPath(API_IDS.PROFILE_CREATE_PARENTAL_PIN);
        const response = await this.http.post<ICreateParentalLockResponse>(path, {
            params: {
                apiKey,
            },
            body: {
                encLock: request.encLock,
                epochMs: String(request.epochMs),
            },
            headers: {
                identityToken,
            },
        });
        return response.data;
    }

    async validateParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encLock: string
    ): Promise<IValidateParentalLockResponse> {
        const path = this.apiPathManager.getPath(API_IDS.PROFILE_VALIDATE_PARENTAL_PIN);
        const response = await this.http.post<IValidateParentalLockResponse>(path, {
            params: {
                apiKey,
            },
            body: {
                encLock,
                epochMs,
            },
            headers: {
                identityToken,
            },
        });
        return response.data;
    }

    async getParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPassword: string
    ): Promise<IGetParentalLockResponse> {
        const response = await this.http.get<IGetParentalLockResponse>(
            this.apiPathManager.getPath(API_IDS.PROFILE_GET_PARENTAL_PIN),
            {
                params: {
                    apiKey,
                },
                headers: {
                    identityToken,
                    epochMs,
                    encPassword,
                },
            }
        );
        return response.data;
    }

    async updateParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPassword: string,
        newEncLock: string
    ): Promise<IUpdateParentalLockResponse> {
        return this.resetParentalLock(apiKey, identityToken, epochMs, newEncLock, { encPassword });
    }

    async resetParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        newEncLock: string,
        options: { encPassword?: string; verificationGuid?: string } = {}
    ): Promise<IUpdateParentalLockResponse> {
        const path = this.apiPathManager.getPath(API_IDS.PROFILE_RESET_PARENTAL_PIN_WITH_OTP);
        const headers: Record<string, string> = {
            identityToken,
            epochMs,
        };
        if (options.encPassword) {
            headers.encPassword = options.encPassword;
        }
        if (options.verificationGuid) {
            headers.verificationGuid = options.verificationGuid;
        }
        const response = await this.http.put<IUpdateParentalLockResponse>(path, {
            params: {
                apiKey,
            },
            headers,
            body: {
                newEncLock,
            },
        });
        return response.data;
    }

    async resetParentalLockGenerateOTP(
        apiKey: string,
        identityToken: string,
        username: string,
        lang: string
    ): Promise<IB2CForgotParentalLockStep1Response> {
        const path = this.apiPathManager.getPath(API_IDS.PROFILE_RESET_PARENTAL_PIN_OTP);
        const response = await this.http.post<IB2CForgotParentalLockStep1Response>(path, {
            params: { apiKey },
            headers: {
                identityToken,
                'Accept-Language': lang,
                'Content-Type': 'application/json',
            },
            body: {},
        });
        return response.data;
    }

    async resetParentalLockVerifyOTP(
        apiKey: string,
        identityToken: string,
        otp: string,
        verificationGuid: string
    ): Promise<IResetParentalLockVerifyOTPResponse> {
        const path = this.apiPathManager.getPath(API_IDS.PROFILE_RESET_PARENTAL_PIN_OTP_VERIFY);
        const response = await this.http.post<IResetParentalLockVerifyOTPResponse>(path, {
            params: { apiKey },
            body: { otp, verificationGuid },
            headers: { identityToken },
        });
        return response.data;
    }

    async resetParentalLockWithOTP(
        apiKey: string,
        identityToken: string,
        pin: string,
        options: { verificationGuid?: string; currentParentalLockPin?: string } = {}
    ): Promise<IUpdateParentalLockResponse> {
        const epochMs = Date.now().toString();
        const resetHeaders: { encPassword?: string; verificationGuid?: string } = {};

        if (options.verificationGuid) {
            resetHeaders.verificationGuid = options.verificationGuid;
        } else if (options.currentParentalLockPin) {
            resetHeaders.encPassword = window.btoa(`${epochMs}:${options.currentParentalLockPin}`);
        }

        return this.resetParentalLock(apiKey, identityToken, epochMs, window.btoa(`${epochMs}:${pin}`), resetHeaders);
    }

    async validatePassword(
        authToken: string,
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPw: string,
        username: string
    ): Promise<IValidatePasswordResponse> {
        const response = await this.http.post<IValidatePasswordResponse>(
            this.apiPathManager.getPath(API_IDS.PROFILE_VALIDATE_PASSWORD),
            {
                params: {
                    profileName: username,
                    apikey: apiKey,
                },
                headers: {
                    Authorization: authToken,
                    epochMs,
                    encPw: encPw,
                    identityToken,
                },
            }
        );
        return response.data;
    }
}

export default ProfileRepository;
